import React, { MouseEventHandler, useCallback } from 'react';
import { Dispatch } from 'src/components-bl/types';
import { useOnLongClick } from 'src/hooks/useOnLongClick';
import { CustomInspirationsGalleryImage } from 'src/services';
import { GalleryImage } from '../../GalleryImage';
import { GridItemStyled } from './GalleryGridItem.styles';
import { SkuConfiguration } from 'src/components-bl/ProductCard/ProductCard';

interface GalleryGridItemProps {
  image: CustomInspirationsGalleryImage;
  index: number;
  onImageClick: (imageId: string) => void;
  onAddTags: (imageId: string) => void;
  onRemoveTagFromImage: (parameters: { imageId: string; tagId: string }) => void;
  onRemoveImage: (imageId: string) => void;
  onImageDrag: (sourceIndex: number, targetIndex: number) => void;
  onImageDrop: () => void;
  dispatch: Dispatch;
  shopId: number;
  onImageLoaded: () => void;
  loading: boolean;
  productInformationLookup: Record<string, SkuConfiguration>;
}

export function GalleryGridItem({
  image,
  index,
  onAddTags,
  onRemoveImage,
  onImageDrag,
  onImageDrop,
  shopId,
  dispatch,
  onImageClick,
  onImageLoaded,
  loading,
  productInformationLookup,
  onRemoveTagFromImage,
}: GalleryGridItemProps): JSX.Element {
  const onShortClick: MouseEventHandler = useCallback(() => {
    onImageClick(image.id);
  }, [image.id, onImageClick]);

  const {
    isLongClick: enableDrag,
    onClick,
    onMouseDown,
    onMouseUp,
  } = useOnLongClick({
    msForLongClick: 100,
    onShortClick,
  });

  const removeTagById = useCallback(
    (tagId: string) => {
      onRemoveTagFromImage({ tagId, imageId: image.id });
    },
    [onRemoveTagFromImage, image.id]
  );

  return (
    <GridItemStyled
      index={index}
      disableDrag={!enableDrag}
      onGridItemDrag={onImageDrag}
      onGridItemDrop={onImageDrop}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    >
      <GalleryImage
        image={image}
        draggable={enableDrag}
        onAddTags={onAddTags}
        onRemoveImage={onRemoveImage}
        dispatch={dispatch}
        shopId={shopId}
        onImageLoaded={onImageLoaded}
        loading={loading}
        productInformationLookup={productInformationLookup}
        removeTagById={removeTagById}
      />
    </GridItemStyled>
  );
}
